import React, { useEffect, useState } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Visual config per milestone tier — single source of truth, keeps the JSX clean.
const TIER_STYLES = {
  first_message:  { ring: "ring-blue-200/80",    bg: "from-blue-50 via-white to-white",        icon: "bg-blue-100 text-blue-700",       chip: "text-blue-700" },
  first_reply:    { ring: "ring-amber-200/80",   bg: "from-amber-50 via-white to-white",       icon: "bg-amber-100 text-amber-700",     chip: "text-amber-700" },
  first_customer: { ring: "ring-emerald-200/80", bg: "from-emerald-50 via-white to-white",     icon: "bg-emerald-100 text-emerald-700", chip: "text-emerald-700" },
  multi_customer: { ring: "ring-violet-300/80",  bg: "from-violet-50 via-fuchsia-50 to-white", icon: "bg-violet-100 text-violet-700",   chip: "text-violet-700" },
};

const fmtSince = (days) => {
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 7) return `${days} days ago`;
  if (days < 30) return `${Math.floor(days / 7)} week${days >= 14 ? "s" : ""} ago`;
  return `${Math.floor(days / 30)} mo ago`;
};

export default function StreakBadge() {
  const [data, setData] = useState(null);
  const [tweetState, setTweetState] = useState("idle"); // idle | copied

  useEffect(() => {
    const token = typeof window !== "undefined" ? localStorage.getItem("p2p_token") : null;
    if (!token) return;
    let cancelled = false;
    axios
      .get(`${API}/me/streak`, { headers: { Authorization: `Bearer ${token}` }, timeout: 8000 })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData(null); });
    return () => { cancelled = true; };
  }, []);

  // Hide entirely if no activity — keep the dashboard clean.
  if (!data || !data.has_activity) return null;

  const style = TIER_STYLES[data.milestone] || TIER_STYLES.first_message;
  const showShare = !!data.share_text;

  const shareToTwitter = () => {
    const text = encodeURIComponent(data.share_text);
    window.open(`https://twitter.com/intent/tweet?text=${text}`, "_blank", "noopener,noreferrer");
  };

  const copyShare = async () => {
    try {
      await navigator.clipboard.writeText(data.share_text);
      setTweetState("copied");
      setTimeout(() => setTweetState("idle"), 1600);
    } catch (_) {
      // Best-effort fallback
      const ta = document.createElement("textarea");
      ta.value = data.share_text;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); setTweetState("copied"); setTimeout(() => setTweetState("idle"), 1600); } catch (e) {}
      document.body.removeChild(ta);
    }
  };

  return (
    <div
      className={`rounded-2xl ring-1 ${style.ring} bg-gradient-to-r ${style.bg} px-5 sm:px-6 py-4 sm:py-5 shadow-[0_8px_28px_-20px_rgba(94,115,193,0.35)]`}
      data-testid="streak-badge"
      data-milestone={data.milestone}
    >
      <div className="flex items-center gap-3 sm:gap-5">
        {/* Big icon — visually anchors the row */}
        <div className={`shrink-0 w-11 h-11 sm:w-14 sm:h-14 rounded-full ${style.icon} flex items-center justify-center text-xl sm:text-3xl`} aria-hidden>
          {data.icon}
        </div>

        {/* Headline + sub — single short line, generous whitespace */}
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className={`font-display text-[15px] sm:text-lg font-bold ${style.chip}`} data-testid="streak-badge-headline">
              {data.headline}
            </span>
            <span className="text-[10.5px] sm:text-[11px] text-zinc-400 font-mono tabular-nums">
              · {fmtSince(data.since_days)}
            </span>
          </div>
          <p className="mt-0.5 text-[12px] sm:text-sm text-zinc-600 leading-snug line-clamp-2" data-testid="streak-badge-sub">
            {data.sub}
          </p>
        </div>

        {/* Share — only after first_reply (don't tempt premature bragging) */}
        {showShare && (
          <div className="hidden sm:flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={shareToTwitter}
              className="inline-flex items-center gap-1.5 text-[12px] font-semibold px-3 py-1.5 rounded-full bg-zinc-900 text-white hover:bg-zinc-700 transition"
              data-testid="streak-badge-share-twitter"
              title="Share this win on X / Twitter"
            >
              <span aria-hidden>𝕏</span> Share
            </button>
            <button
              type="button"
              onClick={copyShare}
              className={`text-[12px] font-semibold px-3 py-1.5 rounded-full transition ${tweetState === "copied" ? "bg-emerald-600 text-white" : "bg-white ring-1 ring-zinc-300 text-zinc-700 hover:bg-zinc-100"}`}
              data-testid="streak-badge-share-copy"
              title="Copy a tweet-ready version"
            >
              {tweetState === "copied" ? "✓ Copied" : "Copy"}
            </button>
          </div>
        )}
      </div>

      {/* Mobile share row — kept on its own line so the headline never wraps */}
      {showShare && (
        <div className="mt-3 flex items-center gap-2 sm:hidden">
          <button
            type="button"
            onClick={shareToTwitter}
            className="inline-flex items-center gap-1.5 text-[12px] font-semibold px-3 py-1.5 rounded-full bg-zinc-900 text-white"
            data-testid="streak-badge-share-twitter-mobile"
          >
            <span aria-hidden>𝕏</span> Share
          </button>
          <button
            type="button"
            onClick={copyShare}
            className={`text-[12px] font-semibold px-3 py-1.5 rounded-full ${tweetState === "copied" ? "bg-emerald-600 text-white" : "bg-white ring-1 ring-zinc-300 text-zinc-700"}`}
            data-testid="streak-badge-share-copy-mobile"
          >
            {tweetState === "copied" ? "✓ Copied" : "Copy"}
          </button>
        </div>
      )}
    </div>
  );
}
